/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  СПРАВОЧНИК УРОВНЕЙ ДОСТУПА текущего мероприятия
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **ЭТО НЕ ПРАВА ДОСТУПА.** Уровень — цифра на бейдже, говорящая человеку
 * на входе, насколько глубоко пускают предъявителя. Ни одна проверка прав в
 * этом приложении не имеет права смотреть сюда: за права отвечают
 * `auth.isAdmin` (система) и `event.canManage` (мероприятие).
 *
 * Соблазн здесь сильнее, чем у зон: «уровень доступа 5» звучит как ступень в
 * иерархии полномочий, а числовая шкала прямо приглашает написать
 * `level >= 3 && canDelete`. Именно поэтому тип называется `AccessLevel`, а не
 * `Level` или `Clearance`, и живёт в отдельном сторе рядом с `accessZones`.
 *
 * ⚠️ **УРОВНИ И ЗОНЫ — ДВА НЕЗАВИСИМЫХ СПРАВОЧНИКА.** Зона отвечает «куда
 * пускают», уровень — «насколько глубоко». Роль держит оба набора сразу и
 * независимо; код, который трактует один как замену другому, потеряет половину
 * бейджа.
 *
 * Справочник локален для мероприятия (шкала одного соревнования ничего не
 * значит на другом) и пагинации не имеет: это десятки записей.
 *
 * ⚠️ Порядок в `items` — **по возрастанию номера**, как его отдал сервер.
 * Пересортировывать на клиенте нельзя, и особенно нельзя сортировать как
 * строки: уровень 10, оказавшийся между 1 и 2, делает шкалу нечитаемой ровно
 * там, где её читают за секунду.
 */

import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import { accessLevelsApi } from '@/api/access-levels'
import type { ApiError } from '@/lib/api-error'
import { normalizeError } from '@/lib/api-error'
import type { AccessLevel, CreateAccessLevelRequest } from '@/types/api'

export const useAccessLevelsStore = defineStore('accessLevels', () => {
  /* ───────────────────────── state ───────────────────────── */

  const eventId = ref<string | null>(null)

  /** Отсортирован сервером по возрастанию номера. */
  const items = ref<AccessLevel[]>([])

  const loading = ref(false)
  const error = shallowRef<ApiError | null>(null)
  const ready = ref(false)

  /* ───────────────────────── getters ─────────────────────── */

  const isEmpty = computed(() => items.value.length === 0)

  const byId = computed(() => new Map(items.value.map((level) => [level.id, level])))

  /**
   * Уровень по id, или `null`.
   *
   * `null` для пустого id и для id, которого в справочнике нет: уровень могли
   * удалить, пока экран был открыт. Падать здесь нечему.
   */
  function get(levelId: string): AccessLevel | null {
    if (!levelId) return null
    return byId.value.get(levelId) ?? null
  }

  /* ───────────────────────── запрос ──────────────────────── */

  let controller: AbortController | null = null

  async function load(id: string, force = false): Promise<void> {
    if (!force && eventId.value === id && ready.value && error.value === null) return

    controller?.abort()
    const ctl = new AbortController()
    controller = ctl

    if (eventId.value !== id) {
      items.value = []
      ready.value = false
    }

    eventId.value = id
    loading.value = true
    error.value = null

    try {
      const data = await accessLevelsApi.list(id, ctl.signal)
      if (controller !== ctl) return
      items.value = data.accessLevels
    } catch (err) {
      if (controller !== ctl) return

      const apiError = normalizeError(err)
      if (apiError.isCanceled) return

      error.value = apiError
      items.value = []
    } finally {
      if (controller === ctl) {
        controller = null
        loading.value = false
        ready.value = true
      }
    }
  }

  /* ───────────────────────── мутации ─────────────────────── */

  /** Бросают `ApiError`; вызывающий заворачивает в `useAsyncAction`. */

  async function create(input: CreateAccessLevelRequest): Promise<AccessLevel> {
    const id = requireEventId()
    const response = await accessLevelsApi.create(id, input)
    // Перечитываем, а не дописываем в конец: сортировка по номеру — серверная,
    // и вставленный руками уровень 3 встал бы после уровня 10.
    await load(id, true)
    return response.accessLevel
  }

  async function update(levelId: string, input: CreateAccessLevelRequest): Promise<AccessLevel> {
    const id = requireEventId()
    const response = await accessLevelsApi.update(id, levelId, input)
    await load(id, true)
    return response.accessLevel
  }

  /**
   * ⚠️ Привязки к ролям уходят каскадом НА СЕРВЕРЕ, и этот стор о них ничего не
   * знает. После удаления вызывающий обязан перечитать справочник ролей —
   * иначе роли продолжат держать id несуществующего уровня.
   */
  async function remove(levelId: string): Promise<AccessLevel> {
    const id = requireEventId()
    const response = await accessLevelsApi.remove(id, levelId)
    await load(id, true)
    return response.accessLevel
  }

  function requireEventId(): string {
    const id = eventId.value
    if (!id) throw new Error('[accessLevels] мутация вне скоупа мероприятия')
    return id
  }

  /* ───────────────────────── $reset ──────────────────────── */

  function $reset(): void {
    controller?.abort()
    controller = null

    eventId.value = null
    items.value = []
    loading.value = false
    error.value = null
    ready.value = false
  }

  return {
    // state
    eventId,
    items,
    loading,
    error,
    ready,
    // getters
    isEmpty,
    byId,
    get,
    // actions
    load,
    create,
    update,
    remove,
    $reset,
  }
})
